const hasOwn = (target, key) => Object.prototype.hasOwnProperty.call(target || {}, key)

// 신규 화면은 location.state를 우선 사용하고, 기존 화면은 레거시 필드도 계속 읽을 수 있게 둔다.
export const getLocationState = (location = {}) => location.state || location

export const parseNumberParam = (value, defaultValue) => {
    if (value === null || value === undefined || value === '') {
        return defaultValue
    }

    const parsedValue = Number(value)

    return Number.isNaN(parsedValue) ? defaultValue : parsedValue
}

export const parseDateParam = (value, fallbackValue) => {
    const fallbackDate = fallbackValue ? new Date(fallbackValue) : new Date()
    const safeFallbackDate = Number.isNaN(fallbackDate.getTime()) ? new Date() : fallbackDate

    if (!value) {
        return safeFallbackDate
    }

    const parsedDate = new Date(value)

    return Number.isNaN(parsedDate.getTime()) ? safeFallbackDate : parsedDate
}

const pickDefinedState = (target = {}) => {
    return Object.entries(target).reduce((nextState, [key, value]) => {
        if (value !== undefined) {
            nextState[key] = value
        }

        return nextState
    }, {})
}

const normalizeParamConfig = (stateKey, config, defaultSearchSelect = {}) => {
    if (typeof config === 'string') {
        return {
            param: config,
            defaultValue: defaultSearchSelect[stateKey],
        }
    }

    return {
        param: config?.param || stateKey,
        defaultValue: hasOwn(config, 'defaultValue') ? config.defaultValue : defaultSearchSelect[stateKey],
        parse: config?.parse,
        serialize: config?.serialize,
        shouldInclude: config?.shouldInclude,
    }
}

const parseSearchParamValue = (rawValue, defaultValue, parse) => {
    if (parse) {
        return parse(rawValue, defaultValue)
    }

    if (rawValue === null || rawValue === undefined) {
        return defaultValue
    }

    return rawValue === '' ? defaultValue : rawValue
}

const shouldIncludeSearchParam = (value, defaultValue) => {
    if (value === undefined || value === null || value === '') {
        return false
    }

    if (defaultValue === undefined || defaultValue === null || defaultValue === '') {
        return true
    }

    return String(value) !== String(defaultValue)
}

// URL query와 location state를 합쳐서 목록의 초기 페이지/검색 조건을 복원한다.
export const createInitialListState = ({
    location,
    defaultSearchSelect = {},
    selectParams = {},
    getExtraState,
}) => {
    const locationState = getLocationState(location)
    const searchParams = new URLSearchParams(location.search)
    const initialSearchText = searchParams.has('text') ? (searchParams.get('text') || '') : (locationState.searchText ?? '')
    const searchSelect = {
        ...defaultSearchSelect,
        ...(locationState.searchSelect || {}),
    }

    Object.entries(selectParams).forEach(([stateKey, config]) => {
        const normalizedConfig = normalizeParamConfig(stateKey, config, defaultSearchSelect)
        const locationValue = locationState.searchSelect?.[stateKey]

        if (searchParams.has(normalizedConfig.param)) {
            searchSelect[stateKey] = parseSearchParamValue(
                searchParams.get(normalizedConfig.param),
                normalizedConfig.defaultValue,
                normalizedConfig.parse
            )
            return
        }

        searchSelect[stateKey] = locationValue ?? normalizedConfig.defaultValue
    })

    return {
        curPage: Math.max(parseNumberParam(searchParams.get('page'), locationState.curPage || 1), 1),
        searchText: initialSearchText,
        searchQuery: initialSearchText,
        searchSelect,
        ...(getExtraState ? getExtraState({ locationState, searchParams }) : {}),
        snackbar: {
            openSnackbar: locationState.openSnackbar,
            severitySnackbar: locationState.severitySnackbar,
            messageSnackbar: locationState.messageSnackbar,
        }
    }
}

// 현재 목록 상태를 다시 URL query로 직렬화해 새로고침과 공유를 가능하게 만든다.
export const buildListSearch = ({
    curPage,
    searchQuery,
    searchSelect = {},
    selectParams = {},
    extraParams = [],
}) => {
    const searchParams = new URLSearchParams()

    if (curPage > 1) {
        searchParams.set('page', String(curPage))
    }

    if (searchQuery) {
        searchParams.set('text', searchQuery)
    }

    Object.entries(selectParams).forEach(([stateKey, config]) => {
        const normalizedConfig = normalizeParamConfig(stateKey, config)
        const value = searchSelect?.[stateKey]
        const includeValue = normalizedConfig.shouldInclude
            ? normalizedConfig.shouldInclude(value, searchSelect)
            : shouldIncludeSearchParam(value, normalizedConfig.defaultValue)

        if (!includeValue) {
            return
        }

        const serializedValue = normalizedConfig.serialize ? normalizedConfig.serialize(value) : value
        searchParams.set(normalizedConfig.param, String(serializedValue))
    })

    extraParams.forEach((config) => {
        const param = config?.param || config?.key

        if (!param) {
            return
        }

        const includeValue = config?.shouldInclude
            ? config.shouldInclude(config.value)
            : config?.value !== undefined && config?.value !== null && config?.value !== ''

        if (!includeValue) {
            return
        }

        const serializedValue = config?.serialize ? config.serialize(config.value) : config.value
        searchParams.set(param, String(serializedValue))
    })

    const nextSearch = searchParams.toString()

    return nextSearch ? `?${nextSearch}` : ''
}

// 목록에서 상세로 이동하거나 상세에서 목록으로 복귀할 때 필요한 컨텍스트만 정리한다.
export const createListNavigationState = (navigationState = {}) => {
    return pickDefinedState(navigationState)
}

// 레거시 화면 호환을 위해 state와 top-level 필드를 함께 넣어 둔다.
export const createListLinkTarget = ({ pathname, search = '', ...navigationState }) => {
    const state = createListNavigationState(navigationState)

    return {
        pathname,
        search,
        state,
        ...state,
    }
}
